import {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import type { StoredJwt } from './types';

const PUBLIC_AUTH_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/logout',
] as const;

const withToken = (
  config: InternalAxiosRequestConfig,
  token: string,
): InternalAxiosRequestConfig => {
  config.headers.Authorization = `Bearer ${token}`;
  return config;
};

const isPublicAuthRequest = (url?: string): boolean => {
  if (!url) {
    return false;
  }

  return PUBLIC_AUTH_PATHS.some((path) => url.includes(path));
};

const isExpired = (err: AxiosError) => err.response?.status === 401;

let isRefreshingToken = false;

export const injectAuthInterceptor = (
  instance: AxiosInstance,
  getJWT: () => StoredJwt | null | undefined,
  refreshToken: () => Promise<StoredJwt | null | undefined>,
  logout: () => Promise<void> | void,
) => {
  instance.interceptors.request.use(
    (config) => {
      if (isPublicAuthRequest(config.url)) {
        return config;
      }

      const token = getJWT();

      if (!token?.access_token) {
        return config;
      }

      return withToken(config, token.access_token);
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!(error instanceof AxiosError) || !isExpired(error)) {
        return Promise.reject(error);
      }

      const config = error.config;

      if (
        isRefreshingToken ||
        isPublicAuthRequest(config?.url) ||
        !getJWT()?.refresh_token
      ) {
        if (isRefreshingToken) {
          logout();
        }

        return Promise.reject(error);
      }

      try {
        isRefreshingToken = true;
        const jwt = await refreshToken();
        isRefreshingToken = false;

        if (jwt?.access_token && config) {
          return instance(withToken(config, jwt.access_token));
        }

        return Promise.reject(error);
      } catch (refreshError) {
        isRefreshingToken = false;
        logout();
        return Promise.reject(refreshError);
      }
    },
  );
};
